import { Box, Group, Stack, Text, ThemeIcon, Title } from "@repo/ui/core";
import {
  IconBuildingFactory,
  IconChartBar,
  IconCheck,
  IconCloud,
  IconCpu,
  IconMessageCircle,
  IconShieldCheck,
} from "@repo/ui/icons";
import type { ComponentType } from "react";
import { motion } from "framer-motion";
import classes from "@styles/onsite.module.css";

type DiscussionItem = {
  text: string;
  icon?: string;
  color?: string;
};

type Props = {
  title: string;
  subtitle?: string;
  items: (string | DiscussionItem)[];
};

const TOPIC_ICONS: Record<string, { icon: ComponentType<{ size?: number }>; color: string }> = {
  "Software Factory": { icon: IconBuildingFactory, color: "cyan" },
  "Cloud Workstation": { icon: IconCloud, color: "blue" },
  "Templates": { icon: IconCheck, color: "green" },
  "Compliance": { icon: IconShieldCheck, color: "yellow" },
  "AAOS": { icon: IconCpu, color: "violet" },
  "Host OS": { icon: IconCpu, color: "teal" },
  "Pilot": { icon: IconChartBar, color: "orange" },
};

function getTopicMeta(text: string): { icon: ComponentType<{ size?: number }>; color: string } {
  for (const [keyword, meta] of Object.entries(TOPIC_ICONS)) {
    if (text.includes(keyword)) return meta;
  }
  return { icon: IconMessageCircle, color: "violet" };
}

export function DiscussionSlide({ title, subtitle, items }: Props) {
  return (
    <Box w="100%" maw={1200} px="xl">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Stack gap="xl">
          <Box>
            <Title
              order={1}
              size="clamp(36px, 5vw, 64px)"
              fw={800}
              style={{ letterSpacing: "-0.02em" }}
            >
              {title}
            </Title>
            {subtitle && (
              <Text size="xl" c="dimmed" mt={8}>
                {subtitle}
              </Text>
            )}
          </Box>

          <Stack gap="md">
            {items.map((item, i) => {
              const text = typeof item === "string" ? item : item.text;
              const meta = getTopicMeta(text);
              const Icon = meta.icon;

              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: 0.2 + i * 0.08 }}
                >
                  <Group
                    gap="md"
                    px="lg"
                    py="md"
                    wrap="nowrap"
                    className={classes.discussionItemCard}
                  >
                    <ThemeIcon
                      size={40}
                      radius="xl"
                      variant="light"
                      color={meta.color}
                      style={{ flexShrink: 0 }}
                    >
                      <Icon size={20} />
                    </ThemeIcon>
                    <Text size="lg" lh={1.5}>
                      {text}
                    </Text>
                  </Group>
                </motion.div>
              );
            })}
          </Stack>
        </Stack>
      </motion.div>
    </Box>
  );
}
